/*
 * AutoLink renders a `NoContextLink` if the link is pointing to a route in the
 * SPA and an `a` if not.
 *
 * props.isToSpa indicates whether the page we're going to is in the SPA. This is true by
 *  default, but THIS COMPONENT IS NOT CHECKING WHETHER THAT IS THE CASE. If you set this
 *  property to true when it is in fact false, it's on you. The prop is here to assist with
 *  map().
 */

import React from 'react'

import {Link} from 'react-router-dom'
import * as URLS from '../urls'
import NoContextLink from './NoContextLink'

const FE_CORE_MIGRATED_URLS = [
  {url: URLS.COLLECTION_TUXEDOS_SUITS, exact: false},
  {url: URLS.COLLECTION_TUXEDOS, exact: false},
  {url: URLS.COLLECTION_SUITS, exact: false},
  {url: URLS.COLLECTION_SHIRTS, exact: false},
  {url: URLS.COLLECTION_VESTS, exact: false},
  {url: URLS.COLLECTION_CUFFLINKS_STUDS, exact: false},
  {url: URLS.COLLECTION_NECKWEAR, exact: false},
  {url: URLS.COLLECTION_SHOES, exact: false},
  {url: URLS.COLLECTION_ACCESSORIES, exact: false},
  {url: URLS.FIT_SIZES, exact: false},
  {url: URLS.FIT_SIZES_SLUG, exact: false},
  {url: URLS.HOME, exact: true},
  {url: URLS.PRODUCT_DETAIL_BUY, exact: false},
  {url: URLS.SHOWROOMS, exact: false},
]

interface AutoLinkProps {
  children: React.ReactNode
  className?: string
  isToSpa?: boolean
  onClick?: (args: unknown) => void
  target?: string
  to: string
  asReactRouterLink?: boolean
  overrideIsToSpa?: boolean
}

/**
  Renders `NoContextLink` (default), native `a`, or a react-router `Link` based on certain critera.
*/
const AutoLink = React.forwardRef<HTMLAnchorElement, AutoLinkProps>(function AutoLink(props, ref) {
  const {
    children,
    className,
    isToSpa = true,
    overrideIsToSpa = false, // single-use hack under time constraints
    onClick = () => null,
    target = '_self',
    asReactRouterLink = false,
    to,
    ...rest
  } = props
  const isMigratedUrl = !!FE_CORE_MIGRATED_URLS.find((u) =>
    u.exact ? to === u.url : to.match(u.url),
  )

  // Hard-reload pages that have been migrated so they are caught by fe-core ISR/SSR.
  if (!overrideIsToSpa && (isMigratedUrl || !isToSpa)) {
    return (
      <a
        className={className}
        href={to}
        onClick={onClick}
        target={target}
        ref={ref as React.Ref<HTMLAnchorElement>}
        {...rest}
      >
        {children}
      </a>
    )
  }

  // Maintain consistency with components that used to use `Link` from `react-router-dom`
  if (asReactRouterLink) {
    return (
      <Link
        to={to}
        className={className}
        onClick={onClick}
        ref={ref as React.Ref<HTMLAnchorElement>}
        {...rest}
      >
        {children}
      </Link>
    )
  }

  return (
    <NoContextLink
      className={className}
      onClick={onClick}
      to={to}
      ref={ref as React.Ref<HTMLAnchorElement>}
      {...rest}
    >
      {children}
    </NoContextLink>
  )
})

export default AutoLink
